import moment from "moment";

import { BillingStatus, Property } from "manager/interfaces/api";

/** One history GET — unbounded changelog dumps timeout on high-churn properties. */
export const BILLING_HISTORY_PAGE_SIZE = 20;

/** PRODUCT-2988 prototype: Onboarding 5, Live 10, Not live 5 (Chase/Hemant). */
export const BILLING_SECTION_PAGE_SIZE = {
  onboarding: 5,
  live: 10,
  notLive: 5,
} as const;

/** Keep Set live / Go live on screen; middle columns can pan. */
export const BILLING_COLUMN_WIDTH = {
  name: 160,
  address: 200,
  portfolio: 140,
  displayOnPortal: 120,
  prequalification: 120,
  numUnits: 130,
  billableUnits: 110,
  liveSince: 140,
  previouslyLive: 120,
  stoppedOn: 110,
  // One column: On, or Off + grey date pill (same chip as scheduled).
  propertyOn: 130,
  // sm "Set live" + 8px pad. Scheduled chip wraps; do not leave a 200px hole.
  action: 112,
} as const;

export const BILLING_STATUS = {
  onboarding: "onboarding",
  live: "live",
  scheduled: "scheduled",
  stopping: "stopping",
  notLive: "not_live",
} as const;

export const ONBOARDING_SECTION_STATUSES = "onboarding,scheduled";
export const LIVE_SECTION_STATUSES = "live,stopping";
export const NOT_LIVE_SECTION_STATUSES = "not_live";

export const SECTION_COPY = {
  onboarding: {
    title: "Onboarding",
    hint: "Not billed yet. Set a property live when it starts taking applications, or schedule a future date.",
    staffOnly: true,
  },
  live: {
    title: "Live",
    hint: "Billed from their go-live date",
    staffOnly: false,
  },
  notLive: {
    title: "Not live",
    hint: "Was billed, then stopped.",
    staffOnly: false,
  },
} as const;

export const STOP_REASONS = ["Contract ended", "Paused", "Other"] as const;
export const STOP_REASON_OTHER = "Other";
/** Matches PropertyBillingWriteSerializer.reason max_length. */
export const BILLING_REASON_MAX_LENGTH = 4000;

/** Chip labels go through as-is. Other is a prompt — send the typed text. */
export const stopBillingReasonFromSelection = (
  selected?: string,
  otherText?: string
): string =>
  selected === STOP_REASON_OTHER
    ? String(otherText || "").trim()
    : String(selected || "").trim();

/** Chase/Jared 117233 + 117236: Stop = Django inactive (no delete) + billing. */
export const STOP_BILLING_SUBTITLE =
  "Marks the property inactive and stops billing. People already in an application can finish. Finance will be notified in Slack.";

/** Chase 117440: Go live after Stop also flips Django Active.
 *  PMS stay as they are — reconnect is a separate ops step, same as admin Activate. */
export const GO_LIVE_AFTER_STOP_HINT =
  "After a stop, go-live must be today or earlier. This also marks the property active again in Django.";

/** First-time Set live: a past date + pill off looks like an upcoming guess.
 *  Today stays quiet — default go-live is already today. */
export const GO_LIVE_EXACT_NOW_TITLE = "Click Go live immediately";
export const GO_LIVE_EXACT_NOW_HINT =
  "You picked a past date. Click Go live immediately so finance can work this now, not as an upcoming change.";

export const comparePropertyName = (
  left?: { rawData?: Pick<Property, "name"> },
  right?: { rawData?: Pick<Property, "name"> }
): number =>
  String(left?.rawData?.name || "").localeCompare(
    String(right?.rawData?.name || "")
  );

export const comparePropertyPortfolio = (
  left?: {
    rawData?: Pick<Property, "portfolioIsDefault" | "portfolioName">;
  },
  right?: {
    rawData?: Pick<Property, "portfolioIsDefault" | "portfolioName">;
  }
): number => {
  const label = (row?: {
    rawData?: Pick<Property, "portfolioIsDefault" | "portfolioName">;
  }) =>
    row?.rawData?.portfolioIsDefault
      ? "No portfolio"
      : String(row?.rawData?.portfolioName || "");
  return label(left).localeCompare(label(right));
};

export const SCHEDULED_CHIP_SUB = "12:00 AM CT";
export const STOPPING_CHIP_SUB = "11:59 PM CT · last billed day";

/** Split a section's comma billing_status list (onboarding,scheduled). */
export const billingStatusTokens = (value?: string): string[] =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

export const sectionStatuses = {
  onboarding: billingStatusTokens(ONBOARDING_SECTION_STATUSES),
  live: billingStatusTokens(LIVE_SECTION_STATUSES),
  notLive: billingStatusTokens(NOT_LIVE_SECTION_STATUSES),
} as const;

export const propertyBillingStatus = (property?: Property): string =>
  property?.billingStatus || BILLING_STATUS.onboarding;

export const STALE_BILLING_MESSAGE =
  "This property changed since the page loaded. Refresh and try again.";

export const formatBillingDate = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  return moment(value).format("MMM D, YYYY");
};

/** Django is_active — not billing Live / Onboarding. Missing = still on. */
export const propertyOnLabel = (
  property?: Pick<Property, "isActive">
): string => (property?.isActive === false ? "Off" : "On");

/** Empty while on — including after reactivate, which clears deactivated_at. */
export const turnedOffOnLabel = (
  property?: Pick<Property, "isActive" | "deactivatedAt">
): string =>
  property?.isActive === false ? formatBillingDate(property.deactivatedAt) : "";

export const formatBillingDateTime = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  return moment(value).format("MMM D, YYYY · h:mm A");
};

export const billingHistoryEventLabel = (event?: string): string => {
  if (event === "went_live") return "Went live";
  if (event === "went_not_live") return "Went not live";
  if (event === "billing_live_at_corrected") return "Go-live date corrected";
  if (event === "units_changed") return "Billable units changed";
  return event || "Billing change";
};

export const billingHistoryDetail = (row?: {
  billingEvent?: string;
  oldBillingUnits?: number | null;
  newBillingUnits?: number | null;
  oldBillingLiveAt?: string | null;
  newBillingLiveAt?: string | null;
  newBillingLiveAtApproximate?: boolean | null;
  oldBillingStoppedAt?: string | null;
  newBillingStoppedAt?: string | null;
}): string => {
  if (!row) {
    return "";
  }
  if (row.billingEvent === "units_changed") {
    if (row.oldBillingUnits == null && row.newBillingUnits == null) {
      return "";
    }
    if (row.oldBillingUnits == null) {
      return `Set to ${row.newBillingUnits}`;
    }
    if (row.newBillingUnits == null) {
      return `Reset to auto (was ${row.oldBillingUnits})`;
    }
    return `${row.oldBillingUnits} → ${row.newBillingUnits}`;
  }
  if (row.billingEvent === "went_live" && row.newBillingLiveAt) {
    const date = `Go-live ${formatBillingDate(row.newBillingLiveAt)}`;
    // new_billing_live_at_approximate: 2766 "show Cartwright immediately".
    return row.newBillingLiveAtApproximate
      ? `${date} · show finance now`
      : date;
  }
  if (row.billingEvent === "went_not_live" && row.newBillingStoppedAt) {
    return `Stop ${formatBillingDate(row.newBillingStoppedAt)}`;
  }
  if (row.billingEvent === "billing_live_at_corrected") {
    const from = formatBillingDate(row.oldBillingLiveAt);
    const to = formatBillingDate(row.newBillingLiveAt);
    if (from && to) {
      return `${from} → ${to}`;
    }
    return to ? `Set to ${to}` : "";
  }
  return "";
};

export const billingStatusLabel = (
  status?: BillingStatus | string,
  property?: Property
): string => {
  if (status === BILLING_STATUS.scheduled) {
    return property?.billingLiveAt
      ? `Goes live ${formatBillingDate(property.billingLiveAt)}`
      : "Scheduled";
  }
  if (status === BILLING_STATUS.stopping) {
    return property?.billingStoppedAt
      ? `Stopping ${formatBillingDate(property.billingStoppedAt)}`
      : "Stopping";
  }
  if (status === BILLING_STATUS.live) {
    return property?.billingLiveAt
      ? `Live since ${formatBillingDate(property.billingLiveAt)}`
      : "Live";
  }
  if (status === BILLING_STATUS.notLive) {
    return "Not live";
  }
  return "Onboarding";
};

export const todayDateInput = (): string => moment().format("YYYY-MM-DD");

export type GoLiveWhenKind = "none" | "past" | "today" | "future";

export const goLiveWhenKind = (dateInput?: string): GoLiveWhenKind => {
  if (!dateInput) {
    return "none";
  }
  const picked = moment(dateInput, "YYYY-MM-DD");
  if (picked.isBefore(moment(), "day")) {
    return "past";
  }
  if (picked.isSame(moment(), "day")) {
    return "today";
  }
  return "future";
};

export const shouldShowGoLiveExactNowHint = (
  dateInput?: string,
  goNow = false,
  restarting = false
): boolean => {
  if (restarting || goNow) {
    return false;
  }
  return goLiveWhenKind(dateInput) === "past";
};

/** “today” when the picked day is today; otherwise “MMM D, YYYY”. */
export const goLiveWhenLabel = (dateInput?: string): string => {
  if (!dateInput) {
    return "";
  }
  if (goLiveWhenKind(dateInput) === "today") {
    return "today";
  }
  return moment(dateInput, "YYYY-MM-DD").format("MMM D, YYYY");
};

// Pill off (default) → approximate. "Go live immediately" is an exact
// date so finance does not treat a known-now go-live as a guessed day.
// Restart after Stop has no pill: they already picked today/past + a
// reason, so the date is exact (Chase 117440 / queue upcoming).
export const billingLiveAtApproximateFromGoNow = (
  goNow?: boolean,
  restarting = false
): boolean => (restarting ? false : !goNow);

export const goLiveApplicantCopy = (
  dateInput?: string,
  goNow = false
): string => {
  const kind = goLiveWhenKind(dateInput);
  if (goNow && kind === "today") {
    return "Import starts when you confirm";
  }
  if (kind === "today") {
    return "Import starts today";
  }
  if (kind === "past") {
    return "Import starts when you confirm";
  }
  if (kind === "future") {
    return `Import starts ${goLiveWhenLabel(dateInput)}, 12:00 AM CT`;
  }
  return "Pick a date";
};

export const goLiveBillingCopy = (
  dateInput?: string,
  goNow = false
): string => {
  const kind = goLiveWhenKind(dateInput);
  if (goNow && kind === "today") {
    return `Starts ${moment().format("h:mm A")} today`;
  }
  if (goNow && kind === "future") {
    return `Saved now. Billing still counts from ${goLiveWhenLabel(
      dateInput
    )}.`;
  }
  if (kind === "today") {
    return "Starts today";
  }
  if (kind === "past") {
    return `Backdated to ${goLiveWhenLabel(dateInput)}`;
  }
  if (kind === "future") {
    return `Saved now as LIVE with go-live ${goLiveWhenLabel(dateInput)}`;
  }
  return "Pick a date";
};

export const goLiveSubmitLabel = (
  dateInput?: string,
  goNow = false,
  count = 1
): string => {
  const kind = goLiveWhenKind(dateInput);
  const todayNow = goNow && kind === "today";
  if (count > 1) {
    if (todayNow) {
      return `Go live now (${count})`;
    }
    if (kind === "future") {
      return `Schedule ${count}`;
    }
    return `Set ${count} live`;
  }
  if (todayNow) {
    return "Go live now";
  }
  if (kind === "future") {
    return "Schedule go-live";
  }
  return "Set live";
};

export const goLiveSlackHint = (): string =>
  "Finance will be notified in Slack.";

export const propertyUnitCount = (property: Property): number =>
  property.platformUnitCount ?? property.numUnits ?? 0;

/** AC + Chase 117236: Auto vs locked Manual count — not Units: Rented / All. */
export const billableUnitsLabel = (property?: Property): string => {
  const manual =
    property?.billableUnitsSource === "manual" ||
    Boolean(property?.billableUnitsIsManual);
  if (!manual) {
    return "Auto";
  }
  if (property?.billableUnits == null) {
    return "Manual";
  }
  return `Manual ${property.billableUnits}`;
};

/** AC: warn only when the billed count differs from the platform count. */
export const billableUnitsDiverge = (
  billed: number,
  platform: number
): boolean => billed !== platform;

export const billableUnitsDivergenceCopy = (
  billed: number,
  platform: number
): string => `Billing ${billed} units. The platform counts ${platform}.`;

export const toBillingDateTime = (dateInput: string): string =>
  moment(dateInput, "YYYY-MM-DD").hour(12).minute(0).second(0).toISOString();

/** Writes for "today" must be now, not noon — noon stays Scheduled/Stopping. */
export const toBillingWriteAt = (dateInput: string): string => {
  if (goLiveWhenKind(dateInput) === "today") {
    return moment().toISOString();
  }
  return toBillingDateTime(dateInput);
};

export const selectedPropertyIds = (
  keys: Array<string | number> | undefined,
  properties: Property[]
): Property[] => {
  const selected = new Set((keys || []).map((key) => String(key)));
  return properties.filter((property) => selected.has(String(property.id)));
};

export const isDateOnOrBeforeToday = (dateInput: string): boolean =>
  moment(dateInput, "YYYY-MM-DD").isSameOrBefore(moment(), "day");

export const isLiveAtOnOrBeforeToday = (liveAt?: string | null): boolean => {
  if (!liveAt) {
    return false;
  }
  return moment(liveAt).isSameOrBefore(moment(), "day");
};

export const canCorrectGoLive = (property: Property): boolean => {
  const status = property.billingStatus;
  return (
    (status === BILLING_STATUS.live || status === BILLING_STATUS.stopping) &&
    isLiveAtOnOrBeforeToday(property.billingLiveAt)
  );
};

export const canCancelScheduled = (property: Property): boolean =>
  property.billingStatus === BILLING_STATUS.scheduled ||
  property.billingStatus === BILLING_STATUS.stopping;

export const canStopBilling = (property: Property): boolean =>
  property.billingStatus === BILLING_STATUS.live ||
  property.billingStatus === BILLING_STATUS.stopping;

export const canSetLive = (property: Property): boolean =>
  property.billingStatus === BILLING_STATUS.onboarding ||
  property.billingStatus === BILLING_STATUS.notLive;

export const partitionSetLiveSelection = (properties: Property[]) => ({
  eligible: properties.filter(canSetLive),
  skipped: properties.filter((property) => !canSetLive(property)),
});

/** Toolbar label — distinct from the per-row "Set live" so tests/screens don't collide. */
export const onboardingBulkSetLiveLabel = (count: number): string =>
  `Set ${count} live`;

/** AC: scheduled / non-Onboarding bulk rows are listed, not dropped silently. */
export const bulkGoLiveSkipOnlyMessage = (skipped: Property[]): string => {
  const names = skipped.map((property) => property.name).filter(Boolean);
  return `Skipped ${skipped.length} (not Onboarding): ${names.join(", ")}.`;
};

export const bulkGoLiveResultMessage = (
  result:
    | { updated?: unknown[]; skipped?: Array<{ id?: number | string }> }
    | undefined,
  properties: Property[]
): string => {
  const updated = result?.updated?.length || 0;
  const skipped = result?.skipped || [];
  if (!skipped.length) {
    return `Set ${updated || properties.length} live.`;
  }
  const names = skipped.map((row) => {
    const match = properties.find(
      (property) => Number(property.id) === Number(row.id)
    );
    return match?.name || `#${row.id}`;
  });
  return `Set ${updated} live. Skipped ${skipped.length}: ${names.join(", ")}.`;
};

export const billingListPage = (data): { rows: Property[]; total: number } => {
  if (Array.isArray(data)) {
    return { rows: data, total: data.length };
  }
  return {
    rows: Array.isArray(data?.results) ? data.results : [],
    total: Number.isFinite(data?.count) ? data.count : 0,
  };
};

export const billingHistoryPage = (data, page, pageSize) => {
  if (Array.isArray(data)) {
    const start = (page - 1) * pageSize;
    return {
      rows: data.slice(start, start + pageSize),
      total: data.length,
    };
  }
  return {
    rows: Array.isArray(data?.results) ? data.results : [],
    total: Number.isFinite(data?.count) ? data.count : 0,
  };
};

export const expectedBillingSnapshot = (property: Property) => {
  // Perf list used to omit raw billing columns. Sending null for a
  // missing key 409s a live Stop (OCC treats "key present + null" as
  // the loaded snapshot). Only send fields the row actually has.
  const snapshot: {
    expectedBillingLifecycle?: string;
    expectedBillingLiveAt?: string | null;
    expectedBillingStoppedAt?: string | null;
    expectedBillableUnits?: number | null;
    expectedBillableUnitsIsManual?: boolean;
  } = {};
  if (typeof property.billingLifecycle === "string") {
    snapshot.expectedBillingLifecycle = property.billingLifecycle;
  }
  if (property.billingLiveAt !== undefined) {
    snapshot.expectedBillingLiveAt = property.billingLiveAt ?? null;
  }
  if (property.billingStoppedAt !== undefined) {
    snapshot.expectedBillingStoppedAt = property.billingStoppedAt ?? null;
  }
  if (property.billableUnits !== undefined) {
    snapshot.expectedBillableUnits = property.billableUnits ?? null;
  }
  if (property.billableUnitsIsManual !== undefined) {
    snapshot.expectedBillableUnitsIsManual = Boolean(
      property.billableUnitsIsManual
    );
  }
  return snapshot;
};

export const bulkGoLiveExpected = (properties: Property[]) =>
  properties.map((property) => ({
    id: property.id,
    ...expectedBillingSnapshot(property),
  }));

export const stopBillingHint = (dateInput?: string): string => {
  const kind = goLiveWhenKind(dateInput);
  if (kind === "none") {
    return "Stops usually come with notice, so this is often the contract end date. It can’t be in the past.";
  }
  if (kind === "past") {
    return "Stop date can’t be backdated. Pick today or a future date.";
  }
  if (kind === "today") {
    return "Stops billing today and marks the property inactive. Applicants already in progress can finish.";
  }
  return `Stays live and billed until ${goLiveWhenLabel(
    dateInput
  )}, then it is marked inactive and billing stops. You can cancel before then.`;
};

export const stopBillingSubmitLabel = (dateInput?: string): string => {
  const kind = goLiveWhenKind(dateInput);
  if (kind === "future") {
    return "Schedule stop";
  }
  if (kind === "today") {
    return "Stop now";
  }
  return "Stop billing";
};
